import { useGraphQLMutation } from '@opengov/bnp-core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { graphql } from '../../../../../gql-generated/hasura';
import {
  DeleteNavigationMenuByIdForOnlinePublicationMutation,
  DeleteNavigationMenuByIdForOnlinePublicationMutationVariables
} from '../../../../../gql-generated/hasura/graphql';
import { HASURA_SERVICE_URL } from '../../../../../config/config';
import { useToast } from '../../../Toast/context/useToast.context';

import { setupOnlinePublicationQueryKeys } from './setupOnlinePublicationQueryKeys';

const DeleteNavigationMenuByIdForOnlinePublicationDocument = graphql(`
  mutation DeleteNavigationMenuByIdForOnlinePublication($id: uuid!) {
    delete_publicationNavigationMenuItems(where: { id: { _eq: $id } }) {
      affected_rows
      returning {
        publicationId
      }
    }
  }
`);

export type UseDeleteNavigationMenuMessages = {
  successTitle: string;
  // `successBody` receives the menu title via callback so the caller can
  // format the locale string (e.g. `The menu "X" was deleted successfully`).
  successBody: (params: { name: string }) => string;
  errorTitle: string;
  errorBody?: string;
};

export type DeleteNavigationMenuParams = {
  id: string;
  title: string;
  publicationId: string;
};

export function useDeleteNavigationMenu(messages: UseDeleteNavigationMenuMessages) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  const mutation = useGraphQLMutation<
    DeleteNavigationMenuByIdForOnlinePublicationMutation,
    DeleteNavigationMenuByIdForOnlinePublicationMutationVariables
  >({
    mutationKey: [...setupOnlinePublicationQueryKeys.all, 'deleteNavigationMenu'],
    document: DeleteNavigationMenuByIdForOnlinePublicationDocument,
    url: HASURA_SERVICE_URL
  });

  const deleteNavigationMenu = useCallback(
    async (params: DeleteNavigationMenuParams): Promise<void> => {
      const { id, title, publicationId } = params;
      try {
        await mutation.mutateAsync({ id });
        await queryClient.invalidateQueries({
          queryKey: setupOnlinePublicationQueryKeys.navigationMenus({ publicationId })
        });
        toggleToast({
          title: messages.successTitle,
          body: messages.successBody({ name: title }),
          severity: 'success'
        });
      } catch {
        toggleToast({
          title: messages.errorTitle,
          body: messages.errorBody,
          severity: 'error'
        });
        throw new Error('Failed to delete navigation menu');
      }
    },
    [mutation, queryClient, messages, toggleToast]
  );

  return {
    deleteNavigationMenu,
    isLoading: mutation.isLoading
  };
}
